import React, { useState, useEffect, useRef } from 'react';
import './ChatPage.scss';
import { MsalAuthenticationTemplate, useMsal } from '@azure/msal-react';
import { InteractionType } from '@azure/msal-browser';
import { loginRequest } from '../../authConfig';
import { PageLayout2 } from '../../Components/PageLayout/PageLayout2';
import { ChatService } from '../../Services/ChatDataService';
import InputModal from '../../Components/InputModal/InputModal';
import { Button, Form } from 'react-bootstrap';
import { parseMarkdownToHtml } from '../../Utils/markdownFormater';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faArrowUp,
    faEdit,
    faTrash,
    faPlus,
    faSearch,
    faBars,
} from '@fortawesome/free-solid-svg-icons';
import ConfirmationModal from '../../Components/ConfirmationModal/ConfirmationModal';
import DynamicChart from '../../Components/DynaicChart/DynamicChart';
import DOMPurify from 'dompurify';

const ChatPageContent = ({ menu }) => {
    const { instance, accounts, inProgress } = useMsal();
    const [conversations, setConversations] = useState([]);
    const [filteredConversations, setFilteredConversations] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

    const [currentConversationId, setCurrentConversationId] = useState(null);
    const [messages, setMessages] = useState([]);
    const [userMessage, setUserMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [file, setFile] = useState(null);
    const [showNewConversationModal, setShowNewConversationModal] = useState(false);
    const [showRenameModal, setShowRenameModal] = useState(false);
    const [conversationToRename, setConversationToRename] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [conversationToDelete, setConversationToDelete] = useState(null);
    const [pendingMessage, setPendingMessage] = useState('');

    const messagesEndRef = useRef(null);
    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        if (inProgress === 'none' && accounts.length > 0) {
            setMessages([]);
            fetchUserConversations();
        }
    }, [accounts, instance, inProgress, menu]);

    useEffect(() => {
        setFilteredConversations(
            conversations.filter((conv) =>
                (conv.description || '').toLowerCase().includes(searchTerm.toLowerCase())
            )
        );
    }, [searchTerm, conversations]);

    const getToken = async () => {
        const request = { ...loginRequest, account: accounts[0] };
        const response = await instance.acquireTokenSilent(request);
        return response.accessToken;
    };

    const fetchUserConversations = async () => {
        const token = await getToken();
        const convs = await ChatService.getUserConversations(menu, token);
        setConversations(convs);
        setFilteredConversations(convs);
    };

    const fetchConversationHistory = async (conversationId) => {
        const token = await getToken();
        const history = await ChatService.getConversationHistory(conversationId, token);
        setMessages(history);
        scrollToBottom();
    };

    const handleCreateNewConversation = async (description) => {
        const token = await getToken();
        const newConv = await ChatService.createConversation(description, menu, token);
        if (newConv) {
            fetchUserConversations();
            setCurrentConversationId(newConv.conversationId);
            setMessages([]);
        }
        if (pendingMessage) {
            setUserMessage(pendingMessage);
            setPendingMessage('');
            await handleSendMessage(new Event('submit'));
        }
        setShowNewConversationModal(false);
    };

    const handleSendMessage = async (e) => {
        e.preventDefault();
        if (!userMessage.trim()) return;

        if (!currentConversationId) {
            setShowNewConversationModal(true);
            setPendingMessage(userMessage);
            setUserMessage('');
            return;
        }

        const newMsgs = [
            ...messages,
            { sender: 'User', messageText: userMessage },
            { sender: 'System', messageText: '<span class="spinner-border spinner-border-sm"></span> Thinking...' },
        ];
        setMessages(newMsgs);
        setUserMessage('');
        scrollToBottom();
        setLoading(true);

        try {
            const token = await getToken();
            const res = await ChatService.sendMessage(currentConversationId, userMessage, token);
            // Normalize backend response
            let aiMessageText;
            if (res && typeof res === "object") {
                aiMessageText = JSON.stringify(res);
            } else if (res && res.aiResponse) {
                aiMessageText = typeof res.aiResponse === "object"
                    ? JSON.stringify(res.aiResponse)
                    : res.aiResponse;
            } else {
                aiMessageText = String(res || "");
            }
            setMessages([...messages, { sender: 'User', messageText: userMessage }, { sender: 'AI', messageText: aiMessageText }]);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
            scrollToBottom();
        }
    };

    const handleConfirmDelete = async (conversationId) => {
        const token = await getToken();
        await ChatService.deleteConversation(conversationId, token);
        setConversations(conversations.filter((c) => c.conversationId !== conversationId));
        if (currentConversationId === conversationId) {
            setCurrentConversationId(null);
            setMessages([]);
        }
        setShowDeleteModal(false);
    };

    const handleRenameConversation = async (description) => {
        const token = await getToken();
        await ChatService.renameConversation(conversationToRename.conversationId, description, token);
        setConversations(
            conversations.map((c) =>
                c.conversationId === conversationToRename.conversationId ? { ...c, description } : c
            )
        );
        setShowRenameModal(false);
        setConversationToRename(null);
    };

    // const renderMessage = (msg, i) => {
    //     const html = parseMarkdownToHtml(msg.messageText);
    //     return (
    //         <div
    //             key={i}
    //             className={`message ${msg.sender === 'User' ? 'user' : 'ai'}`}
    //             dangerouslySetInnerHTML={{ __html: html }}
    //         />
    //     );
    // };

    const renderMessage = (msg, i) => {
        let parsed = null;

        // Try parsing the message JSON
        try {
            parsed = JSON.parse(msg.messageText);
        } catch (e) {
            parsed = null;
        }

        console.log('Parsed message:', parsed);

        // If structured JSON with chartSpec & data
        if (parsed && (parsed.chartSpec || parsed.data)) {
            const { insights, chartSpec, tableHtml, data } = parsed;
            const htmlInsights = parseMarkdownToHtml(insights);

            return (
                <div key={i} className={`message ${msg.sender === "User" ? "user" : "ai"}`}>
                    {insights && (
                        <div className="insights-section mb-3">
                            <h5 className="fw-semibold mb-2">Insights</h5>
                            <p className="text-secondary" dangerouslySetInnerHTML={{ __html: htmlInsights }}></p>
                        </div>
                    )}

                    {chartSpec && data && data.length > 0 && (
                        <div className="chart-section mb-3 p-4">
                            <DynamicChart spec={chartSpec} data={data} />
                        </div>
                    )}

                    {tableHtml && (
                        <div
                            className="table-section"
                            dangerouslySetInnerHTML={{
                                __html: DOMPurify.sanitize(tableHtml),
                            }}
                        />
                    )}
                </div>
            );
        }

        // Fallback: regular text or markdown
        return (
            <div
                key={i}
                className={`message ${msg.sender === "User" ? "user" : "ai"}`}
                dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(msg.messageText),
                }}
            />
        );
    };



    return (
        <div className="chat-page">
            <div className={`conversation-sidebar ${isSidebarCollapsed ? 'collapsed' : ''}`}>
                <div className="sidebar-controls d-flex flex-column gap-2">
                    <Button
                        variant="light"
                        className="sidebar-button w-100"
                        onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                    >
                        <FontAwesomeIcon icon={faBars} />
                        {!isSidebarCollapsed && <span>Toggle Sidebar</span>}
                    </Button>
                    <Button variant='light' size='md' className='sidebar-button w-100' onClick={() => setShowNewConversationModal(true)}>
                        <FontAwesomeIcon icon={faPlus} />
                        {!isSidebarCollapsed && <span>New Conversation</span>}
                    </Button>
                    <Form.Control
                        type="text"
                        placeholder="Search..."
                        className={`sidebar-search ${isSidebarCollapsed ? 'd-none' : ''}`}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        size='sm'
                    />
                </div>

                <div className="conversations-list">
                    {!isSidebarCollapsed && (
                    filteredConversations.map((conv) => (
                        <div
                            key={conv.conversationId}
                            className={`conversation-item ${currentConversationId === conv.conversationId ? 'active' : ''}`}
                            onClick={() => {
                                setCurrentConversationId(conv.conversationId);
                                fetchConversationHistory(conv.conversationId);
                            }}
                        >
                            <span>{conv.description || `Conversation ${conv.conversationId}`}</span>
                            
                                <div className="conversation-item-actions">
                                    <button
                                        className="btn btn-xs btn-outline-dark ms-1"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setConversationToRename(conv);
                                            setShowRenameModal(true);
                                        }}
                                    >
                                        <FontAwesomeIcon icon={faEdit} size="sm" />
                                    </button>
                                    <button
                                        className="btn btn-xs btn-outline-danger ms-1"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setConversationToDelete(conv);
                                            setShowDeleteModal(true);
                                        }}
                                    >
                                        <FontAwesomeIcon icon={faTrash} size="sm" />
                                    </button>
                                </div>
                            
                        </div>
                    ))
                    )}
                </div>
            </div>

            <div className="chat-area">
                <div className="messages">
                    {messages.length > 0 ? messages.map(renderMessage) : (
                        <div className="welcome-message h-100 w-100 d-flex flex-column align-items-center justify-content-center">
                            <h2>Welcome {instance.getActiveAccount().name}</h2>
                            <h2 className="fine-header">Do you need help?</h2>
                        </div>
                    )}
                    <div ref={messagesEndRef} />
                </div>

                <form className="chat-input" onSubmit={handleSendMessage}>
                    <input
                        type="text"
                        value={userMessage}
                        onChange={(e) => setUserMessage(e.target.value)}
                        placeholder="Ask for help"
                        className="form-control"
                        disabled={loading}
                    />
                    <button type="submit" disabled={loading || !userMessage.trim()} className="btn btn-secondary rounded-button ms-3 send-button">
                        <FontAwesomeIcon icon={faArrowUp} />
                    </button>
                </form>
            </div>

            <InputModal
                show={showNewConversationModal}
                onHide={() => { setShowNewConversationModal(false); setPendingMessage(''); }}
                title="New Conversation"
                inputLabel="Enter a name or description for the new conversation"
                onAcceptClick={handleCreateNewConversation}
                onCancelClick={() => setShowNewConversationModal(false)}
            />

            <InputModal
                show={showRenameModal}
                onHide={() => setShowRenameModal(false)}
                title="Rename Conversation"
                inputLabel="Enter a new name or description"
                onAcceptClick={handleRenameConversation}
                onCancelClick={() => setShowRenameModal(false)}
            />

            <ConfirmationModal
                show={showDeleteModal}
                onHide={() => setShowDeleteModal(false)}
                title="Delete Conversation"
                content={`Are you sure you want to delete "${conversationToDelete?.description || 'Unnamed'}"?`}
                onAcceptClick={() => handleConfirmDelete(conversationToDelete?.conversationId)}
                onCancelClick={() => setShowDeleteModal(false)}
            />
        </div>
    );
};

const ChatPage = ({ menu, title }) => (
    <MsalAuthenticationTemplate interactionType={InteractionType.Redirect} authenticationRequest={loginRequest}>
        <PageLayout2 title={title}>
            <ChatPageContent menu={menu} />
        </PageLayout2>
    </MsalAuthenticationTemplate>
);

export default ChatPage;
